import type pdfJS from 'pdfjs-dist/es5/build/pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist/web/pdf_viewer';
import { useEffect, useRef, useState } from 'react';
import useSWR, { MutatorCallback } from 'swr';

import useCache from '../../../../Components/Context/Hooks/useCache';
import { CreditNotePreview, InvoiceInfo, InvoicePreview } from '../../../../Types/api';
import { Invoice, MATCHED_STATUS } from '../../../../Types/invoice';
import { getError, getStatusCode } from '../../../../Utils/errors';
import { HTTP_STATUS_CODES } from '../../../../Utils/errors/NetworkError';
import { captureException } from '../../../../Utils/sentry';
import useGlobalContext from '../../Context';

interface Result {
  loading: boolean;
  error: any | null;
  invoice?: Invoice;
  pdfs?: PDFDocumentProxy[];
  invoicePdf?: PDFDocumentProxy;
  mutate: (data?: MutatorCallback<InvoiceInfo>) => Promise<InvoiceInfo | undefined>;
}

interface CachedPdf {
  data?: PDFDocumentProxy[];
  id: string;
  matchedStatus: MATCHED_STATUS | null;
}

const useInvoice = (invoiceId: string, showCreditNotes: boolean): Result => {
  const pdfsRef = useRef<Map<string, CachedPdf>>(new Map());
  const [cachedPdfs, setCachedPdfs] = useState(pdfsRef.current);
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const { request } = useGlobalContext();

  const addPdf = (pdf: CachedPdf) => {
    pdfsRef.current.set(pdf.id, pdf);
    setCachedPdfs(new Map([...pdfsRef.current]));
  };

  const removePdf = (id: string) => {
    pdfsRef.current.delete(id);
    setCachedPdfs(new Map([...pdfsRef.current]));
  };

  const { getValue } = useCache((invoiceId: string): [string, RequestInit] => [
    '/invoices.info',
    { body: JSON.stringify({ id: invoiceId }) },
  ]);
  const { data, error, mutate } = useSWR<InvoiceInfo>(getValue(invoiceId));
  const invoice = getStatusCode(error) === HTTP_STATUS_CODES.NOT_FOUND ? undefined : data?.data;
  const key = invoice ? (showCreditNotes ? `${invoice.id}-creditNotes` : invoice.id) : null;
  const matchedStatus = invoice && !showCreditNotes ? invoice.matchedStatus : null;

  const getRequests = () => {
    if (!showCreditNotes) {
      return [
        request!<InvoicePreview>('/invoices.download', {
          body: JSON.stringify({
            id: invoice!.id,
          }),
        }),
      ];
    }

    return invoice!.creditNotes.map(({ id }) =>
      request!<CreditNotePreview>('/creditNotes.download', {
        body: JSON.stringify({
          id,
        }),
      }),
    );
  };

  useEffect(() => {
    (async () => {
      if (!key) {
        return;
      }

      const cachedPdf = cachedPdfs.get(key);
      if (cachedPdf && cachedPdf.matchedStatus === matchedStatus) {
        return;
      }

      addPdf({
        id: key,
        matchedStatus,
      });

      let pdfjs: typeof pdfJS;
      let urls: Array<InvoicePreview | CreditNotePreview>;

      const promises: Array<Promise<typeof pdfJS | InvoicePreview | CreditNotePreview>> = [
        import(/* webpackMode: "eager" */ 'pdfjs-dist/es5/build/pdf'),
        ...getRequests(),
      ];

      try {
        const results = await Promise.all(promises);
        pdfjs = results[0] as typeof pdfJS;
        urls = results.slice(1) as Array<InvoicePreview | CreditNotePreview>;
      } catch (e) {
        setPdfError(getError(e));
        removePdf(key);
        captureException(e);
        return;
      }

      pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.js?v=${__PDF_WORKER_VERSION__}`;

      let pdfs: PDFDocumentProxy[] | undefined;
      try {
        pdfs = await Promise.all(urls.map((url) => pdfjs.getDocument({ url: url.data }).promise));
      } catch (exception) {
        captureException(exception);

        setPdfError(getError(exception));
        removePdf(key);
        return;
      }

      setPdfError(null);

      addPdf({
        data: pdfs,
        id: key,
        matchedStatus,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, matchedStatus, request]);

  const errors = error || pdfError;
  const pdfs = key ? cachedPdfs.get(key)?.data : undefined;
  const invoicePdf = invoice ? cachedPdfs.get(invoice.id)?.data?.[0] : undefined;
  if ((!data || !pdfs) && !errors) {
    return { loading: true, error: null, invoice, invoicePdf, mutate };
  }

  return { loading: false, error: errors, invoice, pdfs, invoicePdf, mutate };
};

export default useInvoice;
